/**
 * Choosing which tiles to draw, at which zoom.
 *
 * A distance-driven quadtree: start from a block of coarse tiles and split any
 * tile the viewer is close to, recursively, down to the finest zoom. The leaves
 * of that subdivision are the tiles to draw. Because a tile is either drawn or
 * replaced by its four children — never both — the leaves cover the region
 * exactly once, with no overlap to z-fight and no gap to show through.
 */

import type { LodConfig } from '../../config.js';
import type { GeoOrigin } from '../geo/enu.js';
import { geoToEnu } from '../geo/enu.js';
import { tileSpanMetres, tilesPerAxis } from '../geo/mercator.js';
import type { TileKey } from '../geo/tilekey.js';
import {
  childTiles,
  distanceToTile,
  isTileRowInRange,
  tileAt,
  tileCentre,
  tileExtent,
  tileKeyToString,
  wrapTileX,
} from '../geo/tilekey.js';

/** Where the viewer is, for the purposes of choosing detail. */
export interface ViewerState {
  lat: number;
  lon: number;
  /** Height above the ground in metres. Feeds the split test directly. */
  altitude: number;
}

export interface DesiredTile {
  key: TileKey;
  /** Horizontal distance from the viewer to the tile, in metres. */
  distance: number;
  /** Mesh vertices per axis to build this tile at. */
  gridRes: number;
  /** Skirt depth in metres for this tile's size. */
  skirtDepth: number;
}

/**
 * The tiles that should currently be resident, keyed by `z/x/y`.
 *
 * Altitude is folded into the split test rather than ignored, so a viewer
 * 2.5 km up gets a wide, coarse world instead of a narrow, expensively detailed
 * one — and detail sharpens on its own as they descend.
 */
export function computeDesiredTiles(
  viewer: ViewerState,
  lod: LodConfig,
  origin: GeoOrigin,
): Map<string, DesiredTile> {
  const out = new Map<string, DesiredTile>();
  const viewerEnu = geoToEnu(origin, viewer.lat, viewer.lon);
  const altitude = Math.max(0, viewer.altitude);

  const base = tileAt(viewer.lat, viewer.lon, lod.minZoom);
  const r = lod.radiusTiles;

  for (let dy = -r; dy <= r; dy++) {
    const y = base.y + dy;
    // Rows off the top or bottom of the world simply do not exist. Columns
    // wrap, so they always do.
    if (!isTileRowInRange(y, lod.minZoom)) continue;
    for (let dx = -r; dx <= r; dx++) {
      const x = wrapTileX(base.x + dx, lod.minZoom);
      subdivide({ z: lod.minZoom, x, y }, viewer, viewerEnu, altitude, lod, origin, out);
    }
  }

  return out;
}

function subdivide(
  key: TileKey,
  viewer: ViewerState,
  viewerEnu: { east: number; north: number },
  altitude: number,
  lod: LodConfig,
  origin: GeoOrigin,
  out: Map<string, DesiredTile>,
): void {
  const extent = tileExtent(key, origin);
  const distance = distanceToTile(extent, viewerEnu);

  if (key.z < lod.maxZoom) {
    const span = tileSpanMetres(tileCentre(key).lat, key.z);
    // 3D distance: being high above a tile is as good a reason to leave it
    // coarse as being far to one side of it.
    const viewDistance = Math.hypot(distance, altitude);
    if (viewDistance < lod.splitFactor * span) {
      for (const child of childTiles(key)) {
        subdivide(child, viewer, viewerEnu, altitude, lod, origin, out);
      }
      return;
    }
  }

  const span = tileSpanMetres(tileCentre(key).lat, key.z);
  const gridRes = gridResForZoom(key.z, lod);
  const cellWidth = span / gridRes;
  out.set(tileKeyToString(key), {
    key,
    distance,
    gridRes,
    // Scale the apron with the tile: coarse tiles sit next to finer neighbours
    // that disagree with them by more, so they need to hang further down.
    skirtDepth: Math.max(lod.minSkirt, cellWidth * lod.skirtFactor),
  });
}

/** Mesh density for a tile: full detail only at the finest level. */
export function gridResForZoom(zoom: number, lod: LodConfig): number {
  return zoom >= lod.maxZoom ? lod.gridRes : lod.coarseGridRes;
}

/**
 * True when `key` is inside the region `computeDesiredTiles` would consider at
 * all — used to decide whether a loaded tile is merely at the wrong detail
 * level or has left the world entirely.
 */
export function isWithinLoadedRegion(key: TileKey, viewer: ViewerState, lod: LodConfig): boolean {
  if (key.z < lod.minZoom || key.z > lod.maxZoom) return false;
  const base = tileAt(viewer.lat, viewer.lon, lod.minZoom);
  const shift = key.z - lod.minZoom;
  const atBase = { x: key.x >> shift, y: key.y >> shift };
  const n = tilesPerAxis(lod.minZoom);
  let dx = atBase.x - base.x;
  // Shortest signed difference around the antimeridian.
  if (dx > n / 2) dx -= n;
  if (dx < -n / 2) dx += n;
  return Math.abs(dx) <= lod.radiusTiles && Math.abs(atBase.y - base.y) <= lod.radiusTiles;
}
